<template>
  <q-card flat bordered id="intercom-report-access-sharing">
    <q-card-section class="row items-center">
      <div class="col">
        <div class="text-h6">Sharing Settings</div>
        <div>Allow colleagues and other stakeholders to view and analyze this report</div>
      </div>
      <q-space />
      <div
        v-if="
          invitePeopleList.length ||
          removePeopleList.length ||
          hasNewOwner ||
          usersWithPendingAccessChanges.length
        "
        class="text-primary col-2"
      >
        Pending on save
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section>
      <email-dropdown-select
        id="intercom-share-add-people"
        :model-value="invitePeopleList"
        @update:model-value="(val) => $emit('update:invite-people-list', val)"
        :people-with-access="peopleWithAccess"
        :report-is-in-portfolio="report?.is_in_portfolio"
      />
      <div class="row">
        <q-banner
          v-if="billedPerUnit && invalidInviteList"
          class="q-mt-md fancy-feature-box active text-primary col-12"
          dense
          rounded
          inline-actions
        >
          <template #default>
            <div class="row items-center q-gutter-x-sm no-wrap">
              <q-icon name="info" size="md"></q-icon>
              <div>
                External sharing is disabled. External sharing is only available for properties in
                {{ organization?.display_name }}'s portfolio.
              </div>
            </div>
          </template>
        </q-banner>
        <q-banner
          v-else-if="billedPerSeat && invalidInviteList"
          class="q-mt-md fancy-feature-box active text-primary col-12"
          dense
          rounded
          inline-actions
        >
          <template #default>
            <div class="row items-center q-gutter-x-sm no-wrap">
              <q-icon name="info" size="md"></q-icon>
              <div>
                External sharing is disabled. To add to your subscription, please reach out.
              </div>
            </div>
          </template>
          <template #action>
            <q-btn flat no-caps @click="openIntercom"> Upgrade </q-btn>
          </template>
        </q-banner>
      </div>
      <template v-if="filteredPeopleWithAccess.length">
        <div class="section-title q-mt-md q-mb-sm">
          People with access ({{ filteredPeopleWithAccess.length }})
        </div>
        <q-markup-table flat separator="none" style="max-height: 275px; overflow-y: auto">
          <tbody>
            <tr v-for="u in filteredPeopleWithAccess" :key="u.id || u.email">
              <td style="padding-left: 7px">
                <div class="flex items-center">
                  <q-avatar size="32px" round class="q-mr-sm bg-grey-4">
                    <q-img
                      v-if="u?.picture"
                      :src="u.picture"
                      referrerpolicy="no-referrer"
                      no-spinner
                    />
                    <q-icon v-else name="person" />
                  </q-avatar>
                  <div class="column">
                    <div class="text-weight-medium">{{ userToName(u) }}</div>
                    <div class="row text-caption text-grey-6">
                      {{ u.email }}
                      <div
                        class="q-ml-xs q-pb-xs"
                        v-if="
                          !canExternallyShareValue && !isInternalEmail(u.email, organizationMembers)
                        "
                      >
                        <q-icon name="warning" color="orange">
                          <q-tooltip>
                            This email is not associated with any user in your organization. Please
                            upgrade your account to share reports externally.
                          </q-tooltip>
                        </q-icon>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
              <td
                v-if="invitePeopleList.some((_) => _.id === u.id || _.email === u.email)"
                class="text-right"
                style="padding-right: 7px"
              >
                <div class="row items-center justify-end q-gutter-x-sm">
                  <q-icon name="hourglass_top">
                    <q-tooltip>Pending on save.</q-tooltip>
                  </q-icon>
                  <div
                    v-if="
                      !canExternallyShareValue && !isInternalEmail(u.email, organizationMembers)
                    "
                  >
                    <q-btn-dropdown class="text-orange-7" no-caps flat label="External Email">
                      <q-item
                        v-ripple
                        v-close-popup
                        clickable
                        @click="$emit('update:remove-people-list', u)"
                      >
                        <q-item-section>Remove access</q-item-section>
                      </q-item>
                    </q-btn-dropdown>
                    <q-tooltip anchor="top middle">
                      This email is not associated with any user in your organization. Please
                      upgrade your account to share reports externally.
                    </q-tooltip>
                  </div>
                  <q-btn-dropdown
                    v-else
                    no-caps
                    flat
                    :label="u.read_only ? 'Viewer' : 'Editor'"
                    style="min-width: 100px"
                  >
                    <q-item
                      v-for="option in getSharedWithOptions(u)"
                      :key="option.value"
                      v-close-popup
                      v-ripple
                      clickable
                      :disable="option.disabled"
                      @click="updateUserAccessLevel(u, option.value)"
                    >
                      <q-item-section>
                        <div class="row items-center">
                          <q-icon v-if="option.selected" name="check" class="q-mr-sm" />
                          <!-- Nothing icon to take up space and keep stuff aligned -->
                          <q-icon v-else class="q-mr-sm" />
                          <span>
                            {{ option.label }}
                          </span>
                        </div>
                      </q-item-section>
                      <q-tooltip v-if="option.disabled">{{ option.tooltip }}</q-tooltip>
                    </q-item>
                  </q-btn-dropdown>
                </div>
              </td>
              <td
                v-else-if="u.id !== report?.user_id"
                class="text-right"
                style="padding-right: 7px"
              >
                <div
                  v-if="!canExternallyShareValue && !isInternalEmail(u.email, organizationMembers)"
                >
                  <q-btn-dropdown class="text-orange-7" no-caps flat label="External Email">
                    <q-item
                      v-ripple
                      v-close-popup
                      clickable
                      @click="$emit('update:remove-people-list', u)"
                    >
                      <q-item-section>Remove access</q-item-section>
                    </q-item>
                  </q-btn-dropdown>
                  <q-tooltip anchor="top middle">
                    This email is not associated with any user in your organization. Please upgrade
                    your account to share reports externally.
                  </q-tooltip>
                </div>
                <span v-else-if="!u.id || u.id === u.email" class="text-grey-8 q-mr-sm">
                  <q-icon name="hourglass_top">
                    <q-tooltip>Waiting for an account to be created.</q-tooltip>
                  </q-icon>
                  <span class="cursor-pointer q-ml-sm">
                    <q-icon name="close" @click="$emit('update:remove-people-list', u)" />
                    <q-tooltip>Cancel invite</q-tooltip>
                  </span>
                </span>
                <q-btn-dropdown
                  v-else-if="!organizationMembers.some((_) => _.id === u.id)"
                  no-caps
                  flat
                  label="Viewer"
                >
                  <q-item v-ripple clickable @click="$emit('update:remove-people-list', u)">
                    <q-item-section>Remove access</q-item-section>
                  </q-item>
                </q-btn-dropdown>
                <div v-else class="row items-center justify-end q-gutter-x-sm">
                  <q-icon
                    v-if="
                      usersWithPendingAccessChanges.some(
                        (_) => _.id === u.id || _.email === u.email
                      )
                    "
                    name="hourglass_top"
                  >
                    <q-tooltip>Pending on save.</q-tooltip>
                  </q-icon>
                  <q-btn-dropdown no-caps flat :label="u.read_only ? 'Viewer' : 'Editor'">
                    <q-item
                      v-for="option in getSharedWithOptions(u)"
                      :key="option.value"
                      v-ripple
                      v-close-popup
                      clickable
                      :disable="option.disabled"
                      @click="updateExistingUserAccessLevel(u, option.value)"
                    >
                      <q-item-section>
                        <div class="row items-center">
                          <q-icon v-if="option.selected" name="check" class="q-mr-sm" />
                          <!-- Nothing icon to take up space and keep stuff aligned -->
                          <q-icon v-else class="q-mr-sm" />
                          <span>
                            {{ option.label }}
                          </span>
                        </div>
                      </q-item-section>
                      <q-tooltip v-if="option.disabled">{{ option.tooltip }}</q-tooltip>
                    </q-item>
                    <q-separator />
                    <q-item
                      v-if="allowedToAdminOrOwner"
                      v-ripple
                      v-close-popup
                      clickable
                      @click="changeOwnership(u.id)"
                    >
                      <q-item-section>Transfer ownership</q-item-section>
                    </q-item>
                    <q-item v-ripple clickable @click="$emit('update:remove-people-list', u)">
                      <q-item-section>Remove access</q-item-section>
                    </q-item>
                  </q-btn-dropdown>
                </div>
              </td>
              <td v-else class="text-right text-grey-8" style="padding-right: 7px; font-size: 15px">
                Owner
              </td>
            </tr>
          </tbody>
        </q-markup-table>
      </template>
      <template v-if="canMakePublic || canMakeOrganizationWide">
        <div class="section-title q-mt-md">General access</div>
        <div class="row items-start q-mt-sm q-mb-md q-gutter-md">
          <q-select
            id="intercom-share-general-access"
            v-model="shareSelection"
            transition-duration="100"
            outlined
            :options="shareOptions"
            :option-disable="(opt) => opt.disabled"
            :hint="shareSelection.hint"
            class="col"
            @update:model-value="shareSelectionUpdated"
          >
            <template #selected>
              <div class="text-weight-medium">{{ shareSelection.label }}</div>
            </template>
            <template #prepend>
              <q-avatar :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'" rounded>
                <q-icon :name="shareSelection.icon" />
              </q-avatar>
            </template>
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section avatar class="q-pr-none">
                  <q-avatar
                    :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'"
                    rounded
                    :icon="scope.opt.icon"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-weight-medium">{{ scope.opt.label }}</q-item-label>
                  <q-item-label
                    class="text-grey-8"
                    lines="3"
                    style="max-width: 400px; font-size: small"
                  >
                    {{ scope.opt.hint }}
                  </q-item-label>
                </q-item-section>
                <q-tooltip
                  v-if="scope.opt.disabled && scope.opt.disabledTooltip"
                  anchor="center middle"
                >
                  {{ scope.opt.disabledTooltip }}
                </q-tooltip>
              </q-item>
            </template>
          </q-select>
          <q-select
            class="col-2"
            v-if="shareSelection.index === 2 || shareSelection.index === 1"
            transition-duration="100"
            outlined
            label="Visibility"
            :model-value="shareSelection.value.organization_visibility"
            :options="sharedWithOptions"
            :display-value="getDisplayValue(shareSelection.value.organization_visibility)"
            @update:model-value="updateOrganizationVisibility"
          >
          </q-select>
        </div>
        <q-input
          v-if="shareSelection.index === 2 && report?.public_id"
          v-model="shareableLink"
          dense
          readonly
          class="q-mt-md"
        >
          <template #append>
            <q-btn
              round
              unelevated
              size="sm"
              flat
              no-caps
              color="primary"
              @click="copyShareableLink"
            >
              <q-icon :name="wasCopied ? 'check' : 'content_copy'" size="xs" />
            </q-btn>
          </template>
        </q-input>
      </template>
      <div v-if="isNewDevelopmentReport && isReportOwner" class="col q-mt-lg">
        <div class="section-title q-mt-md">New Development Access</div>
        <div class="row items-start q-mt-xs q-mb-md q-gutter-md">
          <q-toggle
            :model-value="report?.allow_nonowner_development_edits ?? false"
            dense
            label="Allow any user with edit access to this report to edit development property"
            @update:model-value="$emit('update:allow-nonowner-development-edits', $event)"
          />
        </div>
      </div>
    </q-card-section>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { copyToClipboard } from 'quasar'
import {
  type PropertyReportSubscriptionAttributes,
  type PropertyDetailsResponse,
  type AccessUser,
  FeatureFlag,
  OrganizationVisibility
} from 'common'

import { useUserStore } from '@/stores/user'
import EmailDropdownSelect from './EmailDropdownSelect.vue'
import { useStripeStore } from '@/stores/stripe'
import { canExternallyShare, isInternalEmail } from '@/utils.ts'

declare const Intercom: (action: string, ...args: any[]) => void

interface ShareOption {
  index: number
  label: string
  hint: string
  value: {
    is_public: boolean
    organization_visibility: OrganizationVisibility | null
  }
  icon: string
  disabled?: boolean
  disabledTooltip?: string
}
export default {
  name: 'ShareReport',
  components: { EmailDropdownSelect },
  props: {
    report: {
      type: Object as PropType<PropertyReportSubscriptionAttributes | null>,
      default: null
    },
    peopleWithAccess: {
      type: Array as PropType<AccessUser[]>,
      required: true
    },
    removePeopleList: {
      type: Array as PropType<AccessUser[]>,
      required: true
    },
    invitePeopleList: {
      type: Array as PropType<AccessUser[]>,
      required: true
    },
    hasNewOwner: {
      type: Boolean,
      required: true
    },
    // Persisted owner id — not reportCopy.user_id, which changes during a pending transfer
    persistedUserId: {
      type: String as PropType<string | null | undefined>,
      default: null
    },
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    }
  },
  emits: [
    'update:remove-people-list',
    'update:invite-people-list',
    'update:access-level',
    'update:report-owner',
    'update:existing-user-role',
    'update:allow-nonowner-development-edits'
  ],
  data() {
    return {
      shareSelection: {
        index: 0,
        label: 'Restricted',
        hint: 'Private report. Optionally, you can share it with some specific emails by adding them to the list above.',
        value: {
          is_public: false,
          organization_visibility: null
        },
        icon: 'lock_outline'
      } as ShareOption,
      wasCopied: false,
      sharedWithOptions: [
        { label: 'Editor', value: 'editor' },
        { label: 'Viewer', value: 'viewer' }
      ],
      usersWithPendingAccessChanges: [] as AccessUser[],
      originalUserStates: new Map<string, boolean>(), // track original read_only state
      originalPublicId: null as string | null // track original public_id
    }
  },
  computed: {
    allowedToAdminOrOwner() {
      return useUserStore().allowedToAdmin(this.report?.user_id, true)
    },
    canExternallyShareValue() {
      return canExternallyShare(
        this.billedPerUnit,
        this.billedPerSeat,
        this.$auth0.user.value?.internalFeatureFlags,
        this.report?.is_in_portfolio,
        useUserStore().allowedToAdmin(this.report?.user_id, false)
      )
    },
    canMakePublic() {
      if (useUserStore().hasFeatureFlag(null, FeatureFlag.MAKE_PUBLIC)) {
        return this.canExternallyShareValue
      }
      return false
    },
    canMakeOrganizationWide() {
      return (
        useUserStore().hasFeatureFlag(null, FeatureFlag.MAKE_ORGANIZATION_VISIBLE) ||
        useUserStore().allowedToAdmin(this.report?.user_id, false)
      )
    },
    canOrgSharePropertyReports() {
      const store = useUserStore()
      return store.canOrgSharePropertyReports || store.allowedToAdmin(this.report?.user_id, false)
    },
    filteredPeopleWithAccess() {
      return [
        ...this.peopleWithAccess.filter(
          (_) => !this.removePeopleList.some((user) => user.id === _.id || user.email === _.email)
        ),
        ...this.invitePeopleList
      ]
    },
    shareOptions() {
      const allOptions: ShareOption[] = [
        {
          index: 0,
          label: 'Restricted',
          hint: 'Private report. Optionally, you can share it with some specific emails by adding them to the list above.',
          value: {
            is_public: false,
            organization_visibility: null
          },
          icon: 'lock_outline'
        }
      ]

      const userStore = useUserStore()
      const org = userStore.organization
      const organizationMemberCount = userStore.organizationMembersTotal

      if (org && this.canMakeOrganizationWide) {
        allOptions.push({
          index: 1,
          label: org.display_name,
          hint: `Company-wide report. All ${organizationMemberCount} members of ${org.display_name} will be able to view it on their dashboard. Share with additional people by adding them to the list above.`,
          value: {
            is_public: false,
            organization_visibility: OrganizationVisibility.VIEWER
          },
          icon: 'business',
          disabled: !this.canOrgSharePropertyReports,
          disabledTooltip:
            'Your group does not have permission to share reports organization-wide. Contact your organization administrator.'
        })
      }

      if (this.canMakePublic) {
        allOptions.push({
          index: 2,
          label: 'Public',
          hint: org
            ? `Visible to everyone in ${org.display_name} and anyone having access to the public link.  You can change visibility for organization members using the dropdown to the right. Members outside of your organization will always be Viewers.`
            : 'Visible to everyone having access to the public link. You can change visibility for organization members using the dropdown to the right. Users outside of your organization will always be Viewers.',
          value: {
            is_public: true,
            organization_visibility: OrganizationVisibility.VIEWER
          },
          icon: 'public',
          disabled: !this.canOrgSharePropertyReports,
          disabledTooltip:
            'Your group does not have permission to share reports organization-wide. Contact your organization administrator.'
        })
      }

      return allOptions
    },
    shareableLink(): string {
      if (this.report?.public_id) {
        return `${window.location.origin}/property-reports/public/${this.report.public_id}`
      }
      return ''
    },
    organizationMembers(): AccessUser[] {
      return useUserStore().organizationMembers
    },
    billedPerSeat() {
      return useStripeStore().billedPerSeat
    },
    billedPerUnit() {
      return useStripeStore().billedPerUnit
    },
    invalidInviteList() {
      return (
        this.peopleWithAccess.some((user) => {
          if (this.canExternallyShareValue) {
            return false
          }

          return !isInternalEmail(user.email, this.organizationMembers)
        }) ||
        this.invitePeopleList.some((user) => {
          if (this.canExternallyShareValue) {
            return false
          }

          return !isInternalEmail(user.email, this.organizationMembers)
        })
      )
    },
    organization() {
      return useUserStore().organization
    },
    isNewDevelopmentReport() {
      if (this.subjectDetail?.id === this.report?.subject?.id) {
        return !!this.subjectDetail?.user_id
      }
      return !!this.report?.subject?.user_id
    },
    isReportOwner() {
      return useUserStore().user?.sub === this.persistedUserId
    }
  },
  created() {
    this.updateShareOption()
    this.originalPublicId = this.report?.public_id || null
    // Store original states
    this.peopleWithAccess.forEach((user) => {
      const key = user.id || user.email
      this.originalUserStates.set(key, user.read_only || false)
    })
  },
  methods: {
    isInternalEmail,
    userToName(user: AccessUser): string {
      const suffix = user.id === this.$auth0.user.value?.sub ? ' (you)' : ''
      if (user.given_name && user.family_name) {
        return `${user.given_name} ${user.family_name}${suffix}`
      }
      return `${user.email}${suffix}`
    },
    updateUserAccessLevel(user: AccessUser, accessLevel: string) {
      const updatedUser = { ...user, read_only: accessLevel === 'viewer' }
      const userIndex = this.invitePeopleList.findIndex(
        (u) => u.id === user.id || u.email === user.email
      )
      if (userIndex !== -1) {
        const updatedList = [...this.invitePeopleList]
        updatedList[userIndex] = updatedUser
        this.$emit('update:invite-people-list', updatedList)
      }
    },
    updateExistingUserAccessLevel(user: AccessUser, accessLevel: string) {
      const updatedUser = { ...user, read_only: accessLevel === 'viewer' }
      const userKey = user.id || user.email
      const originalReadOnly = this.originalUserStates.get(userKey) || false

      this.$emit('update:existing-user-role', updatedUser)

      // If back to original state, remove from pending changes
      if (updatedUser.read_only === originalReadOnly) {
        this.usersWithPendingAccessChanges = this.usersWithPendingAccessChanges.filter(
          (u) => u.id !== user.id && u.email !== user.email
        )
      } else {
        // Otherwise add/update in pending changes
        const pendingIndex = this.usersWithPendingAccessChanges.findIndex(
          (u) => u.id === user.id || u.email === user.email
        )
        if (pendingIndex !== -1) {
          this.usersWithPendingAccessChanges[pendingIndex] = updatedUser
        } else {
          this.usersWithPendingAccessChanges.push(updatedUser)
        }
      }
    },
    async copyShareableLink() {
      await copyToClipboard(this.shareableLink).then(() => {
        this.wasCopied = true
        setTimeout(() => {
          this.wasCopied = false
        }, 1000)
      })
    },
    changeOwnership(toUserId: string) {
      this.$emit('update:report-owner', toUserId)
    },
    updateShareOption() {
      const organizationVisibility = this.report?.organization_visibility
      const publicId = this.report?.public_id
      if (!organizationVisibility && !publicId) {
        return
      }

      if (publicId) {
        const publicOption = this.shareOptions.find((option) => option.index === 2)
        if (publicOption) {
          this.shareSelection = {
            ...publicOption,
            value: {
              is_public: true,
              organization_visibility: this.report?.organization_visibility || null
            }
          }
        }
      } else if (organizationVisibility) {
        const companyOption = this.shareOptions.find((option) => option.index === 1)
        if (companyOption) {
          this.shareSelection = {
            ...companyOption,
            value: {
              is_public: false,
              organization_visibility: this.report?.organization_visibility || null
            }
          }
        }
      }
    },
    shareSelectionUpdated(visibility: ShareOption) {
      const isPublic = visibility.value.is_public
      const organizationVisibility = visibility.value.organization_visibility

      let publicId = null
      if (isPublic) {
        if (this.originalPublicId) {
          publicId = this.originalPublicId
        } else if (this.report?.public_id) {
          publicId = this.report?.public_id
        } else {
          publicId = crypto.randomUUID()
        }
      }

      this.$emit('update:access-level', organizationVisibility, publicId)
    },
    openIntercom() {
      this.$tracker.openedIntercom()
      Intercom('show')
    },
    getSharedWithOptions(user: AccessUser) {
      // If user is not in organization, only show viewer option
      if (!this.isInternalEmail(user.email, this.organizationMembers)) {
        return [
          {
            label: 'Viewer',
            value: 'viewer',
            disabled: false,
            tooltip: null,
            selected: user.read_only === true
          }
        ]
      }

      const isAdmin = this.organization?.admin_user_ids.includes(user.id!)

      return this.sharedWithOptions.map((option) => ({
        ...option,
        disabled: isAdmin && option.value === 'viewer',
        tooltip: isAdmin && option.value === 'viewer' ? 'Admins always have edit access' : null,
        selected:
          (option.value === 'viewer' && user.read_only === true) ||
          (option.value === 'editor' && user.read_only !== true)
      }))
    },
    updateOrganizationVisibility(selection: { label: string; value: OrganizationVisibility }) {
      if (this.shareSelection.value.organization_visibility) {
        this.shareSelection.value.organization_visibility = selection.value
        this.shareSelectionUpdated(this.shareSelection)
      }
    },
    getDisplayValue(value: OrganizationVisibility | null | undefined) {
      if (!value) {
        return ''
      }
      const optionLabel = this.sharedWithOptions.find((_) => _.value === value)?.label
      if (!optionLabel) {
        return ''
      }
      return optionLabel
    }
  }
}
</script>

<style scoped>
.section-title {
  font-weight: 450;
  font-size: 16px;
}
.modal-title {
  font-size: 20px;
  font-weight: 400;
}
</style>
